import React from "react";

import Button from "../buttons/Button";
import Dialog from "../dialog/Dialog";
import DialogContent from "../dialog/DialogContent";
import DialogFooter from "../dialog/DialogFooter";
import DialogOK from "../dialog/DialogOK";
import DialogCancel from "../dialog/DialogCancel";
import InputFormMandatory from "../inputform/InputFormMandatory";
import Panel from "../panels/Panel";
import AddressEditor from "../addresses/AddressEditor";
import RecordsPanel from "../panels/RecordsPanel";
import { Side } from "../../types/system/Layout";
import PhoneNumberEditor from "../phoneNumbers/PhoneNumberEditor";
import EmailAddressEditor from "../emailaddresses/EmailAddressEditor";
import InputForm from "../inputform/InputForm";
import InputFormField from "../inputform/InputFormField";
import RenderOrientations from "../../types/enums/RenderOrientation";
import { ArrayKeys } from "../../foundation/ArrayKeys";
import Utility from "../../foundation/Utility";
import EmailAddress from "../../types/core/EmailAddress";
import PhoneNumber from "../../types/core/PhoneNumber";
import NumberTypes from "../../types/enums/NumberTypes";
import Address from "../../types/core/Address";
import Contact from "../../types/core/Contact";

import "./ContactEditor.scss";

interface ContactEditorProps {
    value?: Contact;

    editTooltip?: string;

    onChange?: (contact: Contact) => void;
    initialiseGeographyDetails?: () => void;
}

interface ContactEditorState {
    editing?: Contact;
    editOpen?: boolean;
    editLoading?: boolean;
}

class ContactEditor extends React.Component<
    ContactEditorProps,
    ContactEditorState
> {
    state: ContactEditorState = {
        editing: undefined,
        editOpen: false,
        editLoading: false,
    };

    editContact(): void {
        const temp = structuredClone(this.props.value);
        for (let email of temp.emails)
            email.valid = Utility.validEmailAddress(email.address)
                ? "check"
                : "error";
        this.setState({
            editOpen: true,
            editing: temp,
        });
    }

    updateChild<T>(
        type: ArrayKeys<Contact, T>,
        original: T,
        updated: Partial<T>,
    ) {
        if (!this.state.editing) return;

        if (type === "emails") {
            let temp = updated as Partial<EmailAddress>;
            temp.valid = Utility.validEmailAddress(temp.address)
                ? "check"
                : "error";
        }

        this.setState({
            editing: new Contact({
                ...this.state.editing,
                [type]: [
                    ...(this.state.editing[type] as Array<T>).filter(
                        (item: any) => item !== original,
                    ),
                    { ...original, ...updated } as T,
                ],
            }),
        });
    }

    deleteChild<T>(type: ArrayKeys<Contact, T>, record: T) {
        if (!this.state.editing) return;

        this.setState({
            editing: new Contact({
                ...this.state.editing,
                [type]: (this.state.editing[type] as Array<T>).filter(
                    (item: any) => item !== record,
                ),
            }),
        });
    }

    addEmail() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Contact({
                ...this.state.editing,
                emails: [...this.state.editing.emails, new EmailAddress()],
            }),
        });
    }

    addNumber() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Contact({
                ...this.state.editing,
                numbers: [
                    ...this.state.editing.numbers,
                    new PhoneNumber({
                        type: NumberTypes.Phone,
                    }),
                ],
            }),
        });
    }

    addAddress() {
        if (!this.state.editing) return;

        this.setState({
            editing: new Contact({
                ...this.state.editing,
                addresses: [...this.state.editing.addresses, new Address()],
            }),
        });
    }

    addContact() {}

    cancelEdit(): void {
        this.setState({ editOpen: false });
    }

    saveEdit(): void {
        this.setState({ editOpen: false });

        this.props.onChange?.(new Contact(this.state.editing));
    }

    render(): React.ReactNode {
        let classes = "contact-editor";

        return (
            <>
                <div className={classes}>
                    <span>{this.props.value?.display}</span>
                    <span>
                        {
                            (
                                this.props.value?.emails.find(
                                    email => email.primary,
                                ) ?? this.props.value?.emails[0]
                            )?.address
                        }
                    </span>
                    <Button
                        icon="edit"
                        tooltip={this.props.editTooltip ?? "Edit contact"}
                        tooltipPlace="bottom"
                        onClick={this.editContact.bind(this)}
                    />
                </div>

                <Dialog
                    dialogTitle="Edit Contact"
                    showDialog={this.state.editOpen}
                    showLoading={this.state.editLoading}
                    width={700}
                    zIndex={9999}>
                    <DialogContent>
                        <Panel>
                            <Panel>
                                <InputForm
                                    binding={this.state.editing}
                                    display={RenderOrientations.Horizontal}
                                    fieldDisplay={RenderOrientations.Vertical}
                                    onChange={value =>
                                        this.setState({ editing: value })
                                    }
                                    columns={3}>
                                    <InputFormField
                                        label="First Name"
                                        field="firstName"
                                        mandatory={true}
                                    />
                                    <InputFormField
                                        label="Middle Name"
                                        field="middleName"
                                    />
                                    <InputFormField
                                        label="Family Name"
                                        field="lastName"
                                        mandatory={true}
                                    />
                                </InputForm>
                            </Panel>

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Email Address"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.emails ?? []}
                                onAdd={this.addEmail.bind(this)}
                                onDelete={this.deleteChild.bind(this, "emails")}
                                onChange={this.updateChild.bind(this, "emails")}
                                editor={(record, _, update) => (
                                    <EmailAddressEditor
                                        value={record}
                                        valid={record.valid ?? ""}
                                        onChange={update}
                                    />
                                )}
                            />

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Phone Number"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.numbers ?? []}
                                onAdd={this.addNumber.bind(this)}
                                onDelete={this.deleteChild.bind(
                                    this,
                                    "numbers",
                                )}
                                onChange={this.updateChild.bind(
                                    this,
                                    "numbers",
                                )}
                                editor={(record, _, update) => (
                                    <PhoneNumberEditor
                                        value={record}
                                        onChange={update}
                                    />
                                )}
                            />

                            <RecordsPanel
                                inlineRemove={true}
                                noRecordTitle={true}
                                typeName="Address"
                                clearanceSide={Side.Top}
                                records={this.state.editing?.addresses ?? []}
                                onAdd={this.addAddress.bind(this)}
                                onDelete={this.deleteChild.bind(
                                    this,
                                    "addresses",
                                )}
                                onChange={this.updateChild.bind(
                                    this,
                                    "addresses",
                                )}
                                editor={(record, _, update) => (
                                    <AddressEditor
                                        value={record}
                                        onChange={update}
                                    />
                                )}
                            />

                            <InputFormMandatory />
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            text="Cancel"
                            onClick={this.cancelEdit.bind(this)}
                        />
                        <DialogOK
                            text="Save"
                            onClick={this.saveEdit.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

export default ContactEditor;
